/**
 * メッセージ処理関連のユーティリティ関数
 */

import { type RefObject } from 'react';

import Log from '@/Logger';

import { type EWBCIResponse } from '@/LantanaAPI';

import type { EWBCIResponseMessage, EWBCIMessage } from './types';
import { EWBCIError, EWBCIErrorType } from '../utils/errors';
import { DEFAULT_POLLING_INTERVAL, DEFAULT_TIMEOUT } from '../utils/constants';

/**
 * チャンクメッセージボディかどうかを判定
 */
function isChunkedMessageBody(body: object): boolean {
    const b = body as any;
    return typeof b.n === 'number' && typeof b.i === 'number' && typeof b.d === 'string';
}

/**
 * 共通レスポンスボディかどうかを判定
 */
function isCommonResponseBody(b: object): boolean {
    const { status, reason, body } = b as any;
    return typeof status === 'number'
        && (typeof reason === 'undefined' || typeof reason === 'string')
        && (typeof body === 'undefined' || typeof body === 'object');
}

/**
 * サブスクライブイベントボディかどうかを判定
 */
function isSubscribeEventBody(body: object): boolean {
    const b = body as any;
    if (typeof b.pageId !== 'string')
        return false;
    if (typeof b.body !== 'object' || typeof b.body.type !== "string")
        return false;
    switch (b.body.type) {
        case "form":
            if (typeof b.body.form !== 'object') return false;
            break;
        case "pageImage":
            if (typeof b.body.file !== 'string') return false;
            break;
        case "timeout":
        case "conflict":
            if (typeof b.body.message !== 'string') return false;
            break;
        default:
            return false;
    }
    if (typeof b.meta !== 'object' || typeof b.meta.subscribeRequestId !== 'string' || typeof b.meta.subscribeRequestedAt !== 'string')
        return false;
    return true;
}

/**
 * EWBCI メッセージをパースする
 * 
 * @param rawMessage 生のメッセージ
 * @returns パースされたEWBCIメッセージ
 */
export function parseEWBCIMessage(rawMessage: EWBCIResponse): EWBCIMessage {
    const { userId, sessionId, requestId, requestedAt, serialId, deviceId, api } = rawMessage;
    let { responsedAt } = rawMessage;
    if (!userId || !sessionId || !requestId || !requestedAt || !api) {
        throw new Error("Invalid EWBCI message: missing required fields");
    }
    if (!responsedAt) {
        responsedAt = undefined; // 明示的にundefinedにする
    }
    // bodyの型が不明なため、objectかstringかで処理を分ける
    let body: object;
    switch (typeof rawMessage.body) {
        case 'object':
            body = rawMessage.body;
            break;
        case 'string':
            try {
                body = JSON.parse(rawMessage.body);
            } catch (error) {
                throw new Error(`Failed to parse response body: ${error}`);
            }
            break;
        default:
            throw new Error("Invalid EWBCI message: body must be object or string");
    }
    // チャンクメッセージを判別する
    if (isChunkedMessageBody(body)) {
        // チャンクメッセージの場合
        return {
            __typename: 'EWBCIChunkedMessage',
            requestId, userId, sessionId, requestedAt, responsedAt, serialId, deviceId, api,
            body,
        } as any; // EWBCIChunkedMessage型は types.ts で定義済み
    }
    // apiごとに返す型を変える
    switch (api) {
        case 'getdgt':
            return {
                __typename: 'EWBCIGetDGTResponse',
                requestId, userId, sessionId, requestedAt, responsedAt, serialId, deviceId, api,
                body,
            } as any; // EWBCIGetDGTResponse型は types.ts で定義済み
        case 'itpcmd':
            if (!isCommonResponseBody(body as object)) {
                throw new Error("Invalid EWBCI itpcmd message: body is not CommonResponseBody");
            }
            return {
                __typename: 'EWBCIItpcmdResponse',
                requestId, userId, sessionId, requestedAt, responsedAt, serialId, deviceId, api,
                body,
            } as any; // EWBCIItpcmdResponse型は types.ts で定義済み
        case 'file':
            if (!isCommonResponseBody(body as object)) {
                throw new Error("Invalid EWBCI file message: body is not CommonResponseBody");
            }
            return {
                __typename: 'EWBCIFileResponse',
                requestId, userId, sessionId, requestedAt, responsedAt, serialId, deviceId, api,
                body,
            } as any; // EWBCIFileResponse型は types.ts で定義済み
        case 'subscribe':
            if (isSubscribeEventBody(body as object)) {
                return {
                    __typename: 'EWBCIEventMessage',
                    requestId, userId, sessionId, requestedAt, serialId, deviceId, api,
                    body,
                } as any; // EWBCIEventMessage型は types.ts で定義済み
            } else if (isCommonResponseBody(body as object)) {
                return {
                    __typename: 'EWBCISubscribeResponse',
                    requestId, userId, sessionId, requestedAt, responsedAt, serialId, deviceId, api,
                    body,
                } as any; // EWBCISubscribeResponse型は types.ts で定義済み
            } else {
                throw new Error(`Invalid EWBCI subscribe message: unknown body type. ${JSON.stringify(body)}`);
            }
        default:
            throw new Error(`Invalid EWBCI message: unknown api type ${api}`);
    }
}

/**
 * レスポンス待機の設定
 */
interface WaitForResponseOptions {
    /** ポーリング間隔（ミリ秒） */
    intervalMs?: number;
    /** タイムアウト時間（ミリ秒） */
    timeoutMs?: number;
}

/**
 * webからのリクエストに対するレスポンスが返ってくるのをポーリング形式で待つ関数
 * 
 * 見に行く先はresponseResultの中。受け取ったリクエストIDと同様の物が入ってくるまでintervalms毎にチェックする。
 * 暫定としてポーリングする形式にしているが、理想はコールバックを登録しておいて実行する方がいいよね
 * 
 * @param requestId リクエストID
 * @param responseResultRef レスポンス結果の参照
 * @param options 待機オプション
 * @returns レスポンスメッセージ
 */
export async function waitForResponse(
    requestId: string,
    responseResultRef: RefObject<EWBCIResponseMessage[]>,
    options: WaitForResponseOptions = {}
): Promise<EWBCIMessage> {
    const logger = Log.getLogger();
    const {
        intervalMs = DEFAULT_POLLING_INTERVAL,
        timeoutMs = DEFAULT_TIMEOUT
    } = options;

    const start = Date.now();

    return new Promise<EWBCIMessage>((resolve, reject) => {
        const timer = setInterval(() => {
            try {
                const found = responseResultRef.current.find(resp => resp.requestId === requestId);

                if (found) {
                    logger.api.info("EWBCIClient", `Succeeded to receive response for ${requestId}`);
                    clearInterval(timer);
                    resolve(found);
                } else if (Date.now() - start > timeoutMs) {
                    logger.api.warn("EWBCIClient", `Timeout for wait response: ${requestId}`);
                    clearInterval(timer);
                    reject(new EWBCIError(
                        `Timeout for wait response: ${requestId}`,
                        EWBCIErrorType.EwbConnectionError
                    ));
                }
            } catch (error) {
                logger.api.error("EWBCIClient", `Error while waiting for response: ${requestId}`, error);
                clearInterval(timer);
                reject(EWBCIError.fromError(error, EWBCIErrorType.MessageError));
            }
        }, intervalMs);
    });
}

